import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Globe, DollarSign, Wifi, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GridBackground } from '@/components/GridBackground';
import { TopBanner } from '@/components/TopBanner';
import { QuickAnswerSection } from '@/components/QuickAnswerSection';
import { GateFAQSection } from '@/components/GateFAQSection';
import { Footer } from '@/components/Footer';
import { regionalGuides } from '@/data/regionalGuides';

export const RegionalGuidePage = () => {
  const { regionId } = useParams();
  const region = regionalGuides[regionId];

  if (!region) {
    return <div>Region not found</div>;
  }

  // Generate quick answer
  const quickAnswer = `XtreamCube IPTV in ${region.country} provides access to ${Object.values(region.channels).flat().length}+ channels including ${Object.values(region.channels)[0].slice(0, 3).join(', ')}. Pricing: ${region.pricing.typical} with free 24-hour trial. Internet requirement: ${region.internet_requirement}. Best devices: ${region.best_devices.join(', ')}.`;

  // Convert existing FAQ to AIO format
  const faqItems = region.faq.map(item => ({
    question: item.q,
    answer: item.a
  }));

  // Add region-specific FAQs
  const extendedFAQ = [
    ...faqItems,
    {
      question: `What channels are available in ${region.country}?`,
      answer: `${region.country} IPTV includes ${Object.keys(region.channels).length} channel categories: ${Object.keys(region.channels).join(', ')}. Top channels: ${Object.values(region.channels).flat().slice(0, 5).join(', ')}.`
    },
    {
      question: `What is the internet speed requirement in ${region.country}?`,
      answer: `For optimal IPTV streaming in ${region.country}: ${region.internet_requirement}. This ensures smooth HD and 4K playback without buffering on multiple devices.`
    }
  ];

  return (
    <div className="min-h-screen relative">
      <GridBackground />
      <TopBanner />

      <main className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <Button asChild variant="outline" className="mb-8 glass border-primary/30">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <Globe className="w-10 h-10 text-primary" />
              <h1 className="text-4xl sm:text-5xl font-bold text-white">
                IPTV in {region.country}
              </h1>
            </div>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              {region.overview}
            </p>
          </motion.div>

          {/* Quick Answer */}
          <QuickAnswerSection answer={quickAnswer} />

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-12 glass rounded-xl p-6 md:p-8"
            style={{ boxShadow: 'var(--shadow-glass)' }}
          >
            <div className="flex items-center gap-3 mb-6">
              <DollarSign className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-bold text-white">Pricing for {region.country}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-white/60 mb-1">Standard Plans</p>
                <p className="text-2xl font-bold text-primary">{region.pricing.typical}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-white/60 mb-1">Sports Premium</p>
                <p className="text-2xl font-bold text-primary">{region.pricing.sports_premium}</p>
              </div>
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-sm text-white/60 mb-1">Free Trial</p>
                <p className="text-2xl font-bold text-primary">{region.pricing.trial}</p>
              </div>
            </div>
          </motion.div>

          {/* Channels Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12"
          >
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Available Channels
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(region.channels).map(([category, channels], index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                  className="glass rounded-xl p-6"
                  style={{ boxShadow: 'var(--shadow-glass)' }}
                >
                  <h3 className="text-lg font-bold text-white mb-4 capitalize">{category}</h3>
                  <div className="flex flex-wrap gap-2">
                    {channels.map(channel => (
                      <span key={channel} className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">
                        {channel}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Popular Content */}
          {region.popular_content && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 glass rounded-xl p-6"
              style={{ boxShadow: 'var(--shadow-glass)' }}
            >
              <h3 className="text-xl font-bold text-white mb-4">🔥 Popular Content in {region.country}</h3>
              <div className="flex flex-wrap gap-3">
                {region.popular_content.map(content => (
                  <span key={content} className="px-3 py-1 bg-white/10 text-white/90 rounded-lg text-sm">
                    {content}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Requirements & Devices */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="glass rounded-xl p-6" style={{ boxShadow: 'var(--shadow-glass)' }}>
              <div className="flex items-center gap-2 mb-4">
                <Wifi className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-bold text-white">Internet Requirements</h3>
              </div>
              <p className="text-white/80">{region.internet_requirement}</p>
            </div>

            <div className="glass rounded-xl p-6" style={{ boxShadow: 'var(--shadow-glass)' }}>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                <h3 className="text-xl font-bold text-white">Best Devices</h3>
              </div>
              <div className="space-y-2">
                {region.best_devices.map(device => (
                  <div key={device} className="flex items-center gap-2 text-white/80 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    {device}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Legal Notice */}
          {region.legal && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-12 glass rounded-xl p-6"
              style={{ boxShadow: 'var(--shadow-glass)' }}
            >
              <h3 className="text-lg font-bold text-white mb-3">Legal Information</h3>
              <p className="text-white/70 text-sm">{region.legal}</p>
            </motion.div>
          )}

          {/* FAQ */}
          <div className="mt-16">
            <GateFAQSection faqItems={extendedFAQ} />
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-16 text-center glass rounded-2xl p-8"
            style={{ boxShadow: 'var(--shadow-glass)' }}
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Start Streaming in {region.country}
            </h3>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Try XtreamCube IPTV free for 24 hours with all {region.country} channels
            </p>
            <Button asChild className="bg-primary hover:bg-primary/90 text-white">
              <a href="https://xtreamcube.shop/freetrialiptv/" target="_blank" rel="noopener noreferrer">
                Start Free Trial
              </a>
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
